"use client";

import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics";

type ObservedEntry = Pick<
  IntersectionObserverEntry,
  "boundingClientRect" | "intersectionRect" | "rootBounds"
>;

/**
 * Half of what *can* be on screen at once: the section, or the viewport when
 * the section is the taller of the two. `isIntersecting` is true from the first
 * pixel, and a plain 0.5 ratio is unreachable on a viewport shorter than half
 * the section, so neither alone means "seen".
 */
function isHalfSeen(entry: ObservedEntry): boolean {
  const viewportHeight = entry.rootBounds?.height ?? window.innerHeight;
  const seeable = Math.min(entry.boundingClientRect.height, viewportHeight);
  return seeable > 0 && entry.intersectionRect.height >= seeable / 2;
}

/** Steps fine enough that the callback runs near the half-seen point either way. */
const THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];

/**
 * Reports, once per page load, that the letters section was actually seen.
 *
 * Renders nothing and finds its section by id, so the section itself stays a
 * Server Component — the same arrangement as `HeroImpression`.
 */
export function LettersViewTracker({
  targetId,
  letterId,
  locale,
}: {
  targetId: string;
  letterId: string;
  locale: string;
}) {
  // A ref, not effect cleanup, guarantees the single report: StrictMode
  // remounts the effect, and a fresh observer would otherwise report again.
  const sent = useRef(false);

  useEffect(() => {
    const target = document.getElementById(targetId);
    if (target === null || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (sent.current || !entries.some(isHalfSeen)) return;
        sent.current = true;
        trackEvent({ event: "letters_section_view", letterId, locale });
        observer.disconnect();
      },
      { threshold: THRESHOLDS },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [targetId, letterId, locale]);

  return null;
}
